/*
 * Copyright (c) 2024. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet/dist/leaflet.css';
import { type ComponentId, objectEntries } from '@poweralpha/common';
import { useAtom } from 'jotai';
import {
  Circle,
  CircleMarker,
  circle,
  Icon,
  Marker,
  marker,
  type PM,
  Polygon,
  Polyline,
} from 'leaflet';
import markerIconPng from 'leaflet/dist/images/marker-icon.png';
import { merge } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import { drawnLayersAtom } from '../../store';
import type {
  CircleLayer,
  DrawnLayers,
  MarkerLayer,
  PolygonLayer,
  ToolbarLayerProps,
} from '../types';

Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
});

const markerIcon = new Icon({ iconUrl: markerIconPng, iconSize: [25, 41] });

function ToolbarLayer({
  addShapeOptions,
  getPropertiesOnAdd,
  disableAdding,
  options,
  enableEdit,
  disabledButtons,
}: Readonly<ToolbarLayerProps>) {
  const map = useMap();

  const [drawnLayers, setDrawnLayers] = useAtom<DrawnLayers>(drawnLayersAtom);
  const [isEditing, setIsEditing] = useState(false);

  const updateDrawnLayers = useCallback(() => {
    const layers = map.pm.getGeomanLayers();

    const markers: Array<MarkerLayer> = [];
    const circles: Array<CircleLayer> = [];
    const polygons: Array<PolygonLayer> = [];
    layers.forEach((layer) => {
      if (layer instanceof Polygon || layer instanceof Polyline) {
        const latlngs = layer.getLatLngs();
        const id = layer.feature?.properties as ComponentId;
        polygons.push({ latlngs, ...id, polyline: !(layer instanceof Polygon) });
      } else if (layer instanceof Circle) {
        const latlng = layer.getLatLng();
        const radius = layer.getRadius();
        const id = layer.feature?.properties as ComponentId;
        circles.push({ ...layer.options, ...id, center: latlng, radius });
      } else if (layer instanceof Marker || layer instanceof CircleMarker) {
        const latlng = layer.getLatLng();
        const id = layer.feature?.properties as ComponentId;
        markers.push({ ...layer.options, ...id, latlng, circle: layer instanceof CircleMarker });
      }
    });
    const updatedLayers = { markers, circles, polygons };

    setDrawnLayers(updatedLayers);
    return updatedLayers;
  }, [setDrawnLayers, map.pm]);

  const removeAllGeomanLayers = useCallback(
    (...shapes: Array<keyof DrawnLayers>) => {
      const layers = map.pm.getGeomanDrawLayers();

      layers.forEach((layer) => {
        if (
          (shapes.includes('polygons') &&
            (layer instanceof Polygon || layer instanceof Polyline)) ||
          (shapes.includes('circles') && layer instanceof Circle) ||
          (shapes.includes('markers') && (layer instanceof Marker || layer instanceof CircleMarker))
        ) {
          layer.remove();
        }
      });
    },
    [map.pm],
  );

  // Handle creation of a new shape
  const handleCreate: PM.CreateEventHandler = useCallback(
    (e) => {
      const properties = getPropertiesOnAdd?.(e);
      // Add properties (like ID) only to the newly created layer
      if (properties) {
        e.layer = merge(e.layer, { feature: { properties } });
      }

      if (disableAdding?.(e, drawnLayers)) {
        map.pm.disableDraw(); // Disable further drawing
        map.removeLayer(e.layer); // This will remove the last drawn layer
      } else {
        updateDrawnLayers();
      }
    },
    [updateDrawnLayers, disableAdding, getPropertiesOnAdd, drawnLayers, map],
  );

  const handleEdit: PM.EditEventHandler = useCallback(() => {
    updateDrawnLayers();
  }, [updateDrawnLayers]);

  const handleRemove: PM.RemoveEventHandler = useCallback(() => {
    updateDrawnLayers();
  }, [updateDrawnLayers]);

  useEffect(() => {
    // Detect when the user enters or exits drawing mode
    map.on('pm:globaldrawmodetoggled', (e) => {
      setIsEditing(e.enabled);
    });

    // Detect when the user enters or exits editing mode
    map.on('pm:globaleditmodetoggled', (e) => {
      setIsEditing(e.enabled);
    });

    map.on('pm:globalremovalmodetoggled', (e) => {
      setIsEditing(e.enabled);
    });

    return () => {
      // Cleanup added markers and events on component unmount
      map.off('pm:globaldrawmodetoggled');
      map.off('pm:globaleditmodetoggled');
      map.off('pm:globalremovalmodetoggled');
    };
  }, [map]);

  useEffect(() => {
    if (!drawnLayers.markers)
      return () => {
        /*(nothing)*/
      };
    removeAllGeomanLayers('markers');

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: 17/15 complexity
    const addedMarkers = drawnLayers.markers.map((m) => {
      const layer = marker(m.latlng, {
        ...m,
        icon: m.icon ?? addShapeOptions?.getIcon?.(m) ?? markerIcon,
      }).addTo(map);

      if (layer.feature) {
        layer.feature.properties = { id: m.id };
      }

      if ((m.click || addShapeOptions?.click) && !isEditing) {
        layer.on('click', (e) => m.click?.(e) ?? addShapeOptions?.click?.(e));
      }
      if (m.tooltip || addShapeOptions?.tooltip) {
        layer.bindTooltip(m.tooltip ?? addShapeOptions?.tooltip ?? '');
      }
      if (m.draggable) {
        // Enable Geoman for the marker
        layer.pm.enable({ draggable: true, allowSelfIntersection: false });

        layer.dragging?.enable();
      }

      layer.on('pm:remove', (e) => {
        handleRemove(e); // Call callback when a layer is removed
      });

      layer.on('pm:edit', (e) => {
        handleEdit(e); // Call callback when a layer is removed
      });

      return layer;
    });

    if (addedMarkers.length === 0) {
      removeAllGeomanLayers('markers');
    }
    return () => {
      addedMarkers.forEach((marker) => {
        marker.remove();
      });
    };
  }, [
    map,
    drawnLayers.markers,
    handleRemove,
    handleEdit,
    removeAllGeomanLayers,
    isEditing,
    addShapeOptions,
  ]);

  useEffect(() => {
    if (!drawnLayers.circles)
      return () => {
        /*(nothing)*/
      };
    removeAllGeomanLayers('circles');

    const addedMarkers = drawnLayers.circles.map((m) => {
      const layer = circle(m.center, m.radius ?? 50e3, m).addTo(map);

      if (layer.feature) {
        layer.feature.properties = { id: m.id };
      }

      if ((m.click || addShapeOptions?.click) && !isEditing) {
        layer.on('click', (e) => m.click?.(e) ?? addShapeOptions?.click?.(e));
      }
      if (m.tooltip || addShapeOptions?.tooltip) {
        layer.bindTooltip(m.tooltip ?? addShapeOptions?.tooltip ?? '');
      }

      layer.on('pm:remove', (e) => {
        handleRemove(e); // Call callback when a layer is removed
      });

      layer.on('pm:edit', (e) => {
        handleEdit(e); // Call callback when a layer is removed
      });

      return layer;
    });

    if (addedMarkers.length === 0) {
      removeAllGeomanLayers('circles');
    }
    return () => {
      addedMarkers.forEach((marker) => {
        marker.remove();
      });
    };
  }, [
    map,
    drawnLayers.circles,
    removeAllGeomanLayers,
    handleRemove,
    handleEdit,
    isEditing,
    addShapeOptions,
  ]);

  useEffect(() => {
    map.on('pm:create', handleCreate);

    return () => {
      map.off('pm:create', handleCreate);
    };
  }, [handleCreate, map]);

  useEffect(() => {
    map.on('pm:edit', handleEdit);

    return () => {
      map.off('pm:edit', handleEdit);
    };
  }, [handleEdit, map]);

  useEffect(() => {
    map.on('pm:remove', handleRemove);

    return () => {
      map.off('pm:remove', handleRemove);
    };
  }, [handleRemove, map]);

  useEffect(() => {
    // add/hide the Geoman toolbar
    if (enableEdit) {
      map.pm.addControls({ ...options, editControls: enableEdit, position: 'topright' });
    } else {
      map.pm.removeControls();
    }
  }, [enableEdit, options, map.pm]);

  useEffect(() => {
    // on initial load
    updateDrawnLayers();
  }, [updateDrawnLayers]);

  useEffect(() => {
    if (!disabledButtons) return;
    objectEntries(disabledButtons).forEach(([key, value]) => {
      map.pm.Toolbar.setButtonDisabled(key, value);
    });
  }, [disabledButtons, map.pm.Toolbar]);

  return null;
}

export default ToolbarLayer;
